'use client';

import { useState } from 'react';
import { signIn } from 'next-auth/react';
import PopupTemplate from '@/app/ui/popupTemplate';
import { useTranslations } from 'next-intl';
import { Languages } from 'lucide-react';
import LanguageSelectPopup from '@/app/user-and-session/languages/ui/LanguageSelectPopup';
import { useLocale } from '@/app/user-and-session/languages/i18n';
import SUPPORTED_LANGUAGES from '@/app/user-and-session/languages/languages';

interface LoginPopupProps {
  isOpen: boolean;
  onClose: () => void;
  redirectTarget?: string; // Optional redirect target URL
}

export default function LoginPopup({
  isOpen,
  onClose,
  redirectTarget = '/'
}: LoginPopupProps) {
  const t = useTranslations('popup.login');
  const { locale } = useLocale();
  const languageName = SUPPORTED_LANGUAGES[locale] || SUPPORTED_LANGUAGES['en'];
  const [showLanguagePopup, setShowLanguagePopup] = useState(false);

  const handleGoogleLogin = () => {
    signIn('google', { callbackUrl: redirectTarget });
  };

  const handleDiscordLogin = () => {
    signIn('discord', { callbackUrl: redirectTarget });
  };

  return (
    <>
      <PopupTemplate
        isOpen={isOpen}
        onClose={onClose}
        closeable={true}
        isTransparent={true}
      >
        <div className="p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-white mb-3">{t('welcomeTitle')}</h1>
            <p className="text-gray-300 text-sm leading-relaxed">
              {t('welcomeDescription')}
            </p>
          </div>

          {/* Language Selector */}
          <div className="mb-6 flex items-center justify-center">
            <button
              onClick={() => setShowLanguagePopup(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#ffffff1a] hover:bg-[#ffffff33] rounded-xl text-white transition-colors duration-200"
              aria-label="Change Language"
            >
              <Languages className="w-4 h-4 text-gray-300" />
              <span className="text-sm font-medium font-['Montserrat',Helvetica]">
                {languageName}
              </span>
            </button>
          </div>

          {/* Login buttons */}
          <div className="space-y-4">
        {/* Discord Login */}
        <button
          onClick={handleDiscordLogin}
          className="w-full flex items-center justify-center gap-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 px-4 rounded-xl transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
          </svg>
          <span>{t('continueWithDiscord')}</span>
        </button>
        {/* Google Login */}
        <button
          onClick={handleGoogleLogin}
          className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-100 text-gray-900 font-medium py-3 px-4 rounded-xl transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          <span>{t('continueWithGoogle')}</span>
        </button>
        </div>
      </div>
    </PopupTemplate>

    {/* Language Selection Popup */}
    <LanguageSelectPopup
      isOpen={showLanguagePopup}
      onClose={() => setShowLanguagePopup(false)}
    />
    </>
  );
}
